import React, { useState, useEffect } from 'react';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { useAuth } from '../auth/AuthContext';
import { apiClient } from '../services/apiClient';

export default function AdminUsers() {
  const { user } = useAuth();
  const [whitelistUsers, setWhitelistUsers] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [email, setEmail] = useState('');
  const [formError, setFormError] = useState(null);
  const [formSuccess, setFormSuccess] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isValidEmail, setIsValidEmail] = useState(true);

  useEffect(() => {
    fetchWhitelistUsers();
  }, []);

  const fetchWhitelistUsers = async () => {
    try {
      const data = await apiClient.get('/api/admin/users/whitelist');
      setWhitelistUsers(data);
    } catch (error) {
      console.error('Error fetching whitelisted users:', error);
      setFormError('Failed to load whitelisted users');
    }
  };

  const handleOpenDialog = () => {
    setOpenDialog(true);
    setEmail('');
    setFormError(null);
    setFormSuccess(false);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEmail('');
    setFormError(null);
    setFormSuccess(false);
  };

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(String(email).toLowerCase());
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    setIsValidEmail(validateEmail(value));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isValidEmail) return;

    try {
      setSaving(true);
      setFormError(null);
      setFormSuccess(false);

      await apiClient.post('/api/admin/users/whitelist', { email });

      setFormSuccess(true);
      handleCloseDialog();
      fetchWhitelistUsers();
    } catch (error) {
      console.error('Error whitelisting user:', error);
      if (error.response?.status === 409) {
        setFormError('Cet email est déjà dans la whitelist');
      } else {
        setFormError('Impossible d\'ajouter l\'utilisateur à la whitelist');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (email) => {
    try {
      setSaving(true);
      setFormError(null);

      await apiClient.delete(`/api/admin/users/whitelist/${encodeURIComponent(email)}`);
      fetchWhitelistUsers();
    } catch (error) {
      console.error('Error removing user from whitelist:', error);
      setFormError('Impossible de retirer l\'utilisateur de la whitelist');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="bg-gray-100 rounded-lg">
      <div className="px-6 py-4">
        <h2 className="text-lg font-medium text-gray-900">
          Administrateurs whitelistés
        </h2>
      </div>

      <div className="px-6 py-4">
        <div className="flex justify-end mb-6">
          <button
            onClick={handleOpenDialog}
            className="inline-flex items-center justify-center rounded-md bg-[rgb(141,212,209)] px-4 py-2 text-sm font-medium text-gray-700 hover:bg-[rgb(121,192,189)] focus:outline-none focus:ring-2 focus:ring-[rgb(141,212,209)] focus:ring-offset-2"
          >
            <AddIcon className="w-5 h-5 mr-2" />
            Ajouter un administrateur
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ajouté par
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ajouté le
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {whitelistUsers.map((whitelistUser) => (
                <tr key={whitelistUser.email} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {whitelistUser.email}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {whitelistUser.added_by}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatDate(whitelistUser.added_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => handleDelete(whitelistUser.email)}
                      className="text-red-600 hover:text-red-900"
                      disabled={saving}
                    >
                      <DeleteIcon className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Add User Dialog */}
        {openDialog && (
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50 ml-[240px]">
            <div className="bg-white rounded-lg max-w-md w-full">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">
                  Ajouter un administrateur à la whitelist
                </h3>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="px-6 py-4">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      id="email"
                      value={email}
                      onChange={handleEmailChange}
                      className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm
                        ${isValidEmail ? 'border-gray-300 focus:border-blue-500 focus:ring-blue-500' : 'border-red-300 focus:border-red-500 focus:ring-red-500'}`}
                      required
                    />
                    {!isValidEmail && email && (
                      <p className="mt-1 text-sm text-red-600">
                        Veuillez entrer une adresse email valide
                      </p>
                    )}
                  </div>
                </div>

                <div className="px-6 py-4 bg-gray-50 flex justify-end space-x-3 rounded-b-lg">
                  <button
                    type="button"
                    onClick={handleCloseDialog}
                    className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    disabled={saving || !isValidEmail}
                    className={`inline-flex justify-center rounded-md bg-[rgb(141,212,209)] px-4 py-2 text-sm font-medium text-gray-700 
                      ${!saving && isValidEmail ? 'hover:bg-[rgb(121,192,189)]' : 'opacity-75 cursor-not-allowed'}
                      focus:outline-none focus:ring-2 focus:ring-[rgb(141,212,209)] focus:ring-offset-2`}
                  >
                    {saving ? 'Ajout en cours...' : 'Ajouter'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {formError && (
          <div className="mt-4 p-4 rounded-md bg-red-50 text-sm text-red-700">
            {formError.replace(/utilisateur/gi, 'administrateur')}
          </div>
        )}

        {formSuccess && (
          <div className="mt-4 p-4 rounded-md bg-green-50 text-sm text-green-700">
            Administrateur ajouté avec succès
          </div>
        )}
      </div>
    </div>
  );
} 